import React from "react";
import { Badge, Layout, Menu, Row, Col, Dropdown } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { useHistory, useLocation } from "react-router-dom";
import { PricingAPI } from "../../controller/PricingAPI";
import { NAV_ITEMS, hasPermission, visibleGroups } from "../../navigation";
import { TODOS_CHANGED_EVENT } from "./pricing/TodoPage/todoHelpers";
import ChangePasswordModal from "../components/ChangePasswordModal";
import ErrorBoundary from "../components/ErrorBoundary";

const SIDEBAR_COLLAPSED_KEY = "sidebarCollapsed";

function readStoredCollapsed() {
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
  } catch (e) {
    return false;
  }
}

function writeStoredCollapsed(collapsed) {
  try {
    window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
  } catch (e) {
    // 隱私模式等情境下 localStorage 不可用，忽略即可
  }
}

export default function DefaultLayout({ children }) {
  const [breakpoint, setBreakpoint] = React.useState(false);
  // 收合狀態存在 localStorage，重新載入頁面才不會彈回展開。
  // (DefaultLayout 包在 Switch 外層，換頁本身不會重新掛載這一層)
  const [collapsed, setCollapsed] = React.useState(readStoredCollapsed);
  const [passwordModalVisible, setPasswordModalVisible] = React.useState(false);
  const { Header, Content, Sider } = Layout;
  const history = useHistory();
  const location = useLocation();
  const handleLogout = () => {
    history.push('/');
    PricingAPI.auth.logout();
  };
  const handleCollapse = (nextCollapsed, type) => {
    // antd 的 responsive handler 在「螢幕夠寬」時 (含桌機掛載當下) 會強制展開，
    // 直接套用會蓋掉使用者記住的收合偏好；改成回到儲存的偏好值
    if (type === "responsive" && !nextCollapsed) {
      setCollapsed(readStoredCollapsed());
      return;
    }
    setCollapsed(nextCollapsed);
    // 只記住桌機的手動操作，避免小螢幕的自動收合污染桌機偏好
    if (type === "clickTrigger" && !breakpoint) {
      writeStoredCollapsed(nextCollapsed);
    }
  };
  const menuGroups = React.useMemo(
    () => visibleGroups(PricingAPI.auth.getPermissions()),
    []
  );
  // 側邊欄「待辦事項」的角標：與「待我處理」同一個範圍 (指派給我的，加上無主工作)，
  // 所以正式站上線後會是 144。有逾期就用紅色。顯示文字受 antd Badge 預設
  // overflowCount=99 影響會顯示 99+，真實數字在 title 屬性裡
  const [todoSummary, setTodoSummary] = React.useState({ open: 0, overdue: 0 });
  const canSeeTodos = React.useMemo(
    () => hasPermission(PricingAPI.auth.getPermissions(), "work.todos"),
    []
  );
  const refreshTodoSummary = React.useCallback(() => {
    if (!canSeeTodos) {
      return;
    }
    PricingAPI.todos.summary()
      .then((response) => setTodoSummary(response.data))
      .catch(() => {
        // 角標是輔助資訊，抓不到就維持原數字，不打擾使用者
      });
  }, [canSeeTodos]);

  React.useEffect(() => {
    refreshTodoSummary();
  }, [refreshTodoSummary, location.pathname]);

  React.useEffect(() => {
    window.addEventListener(TODOS_CHANGED_EVENT, refreshTodoSummary);
    return () => window.removeEventListener(TODOS_CHANGED_EVENT, refreshTodoSummary);
  }, [refreshTodoSummary]);

  const selectedKeys = React.useMemo(() => {
    const path = location.pathname === "/" ? "/dashboard" : location.pathname;
    // 最長前綴 (以路徑段為界)：/todos/123 要高亮 todos，而 /inventory/rebalance
    // 要高亮 rebalance 而不是它的前綴 inventory，所以不能取第一個命中的
    const matched = NAV_ITEMS
      .filter((item) => path === item.path || path.startsWith(`${item.path}/`))
      .sort((x, y) => y.path.length - x.path.length)[0];
    return matched ? [matched.key] : [];
  }, [location.pathname]);
  const styles = breakpoint ? { position: 'absolute', zIndex: 99, height: '100%' } : {};
  return (
    <Layout className="full-page" style={{ height: '100%' }}>
      <Sider
        collapsible
        collapsed={collapsed}
        onBreakpoint={setBreakpoint}
        onCollapse={handleCollapse}
        breakpoint="lg"
        collapsedWidth={breakpoint ? 0 : 80}
        style={styles}
      >
        <div className="sider-nav">
          <div className={collapsed ? "sider-logo sider-logo-collapsed" : "sider-logo"}>
            {collapsed ? "禾風" : "禾風新棧決策支援系統"}
          </div>
          <Menu mode="inline" theme="dark" selectedKeys={selectedKeys}>
            {menuGroups.map((group) => (
              <Menu.ItemGroup key={group.title} title={group.title}>
                {group.items.map((item) => (
                  <Menu.Item key={item.key} icon={item.icon}>
                    <Link to={item.path}>
                      {item.label}
                      {item.key === "todos" && todoSummary.open > 0 && (
                        <Badge
                          count={todoSummary.open}
                          size="small"
                          style={{
                            marginLeft: 8,
                            backgroundColor: todoSummary.overdue > 0 ? "#ff4d4f" : "#1890ff",
                            boxShadow: "none",
                          }}
                        />
                      )}
                    </Link>
                  </Menu.Item>
                ))}
              </Menu.ItemGroup>
            ))}
          </Menu>
        </div>
      </Sider>
      <Layout>
        {breakpoint && !collapsed && (
          // 點遮罩收合選單；小螢幕的收合不寫入 localStorage，避免污染桌機偏好
          <div
            onClick={() => setCollapsed(true)}
            style={{ position: 'absolute', width: '100%', height: '100%', opacity: 0.5, backgroundColor: 'black', zIndex: 98, cursor: 'pointer' }}
          ></div>
        )}
        <Header style={{ padding: '0 20px' }}>
          <Row justify="end">
            <Col>
              <Dropdown
                overlay={
                  <Menu>
                    <Menu.Item key="password" onClick={() => setPasswordModalVisible(true)}>
                      修改密碼
                    </Menu.Item>
                    <Menu.Item key="logout" onClick={handleLogout}>
                      登出
                    </Menu.Item>
                  </Menu>
                }
              >
                <span style={{ cursor: 'pointer' }}>
                  {PricingAPI.auth.getLoginUser().email} <DownOutlined />
                </span>
              </Dropdown>
            </Col>
          </Row>
        </Header>
        {/* 邊界只包內容區：頁面出錯時選單與標題列要留著，人才能直接切去別頁，
            而不是整站變白只剩重新載入一條路。resetKey 帶路徑，換頁即復原。 */}
        <Content style={{ padding: 15, overflow: 'hidden', overflowY: 'auto' }}>
          <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>
        </Content>
      </Layout>
      <ChangePasswordModal
        visible={passwordModalVisible}
        onClose={() => setPasswordModalVisible(false)}
      />
    </Layout>
  );
}
